import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Couple, Contribution} from '../../types';
import {  SupportedCurrency } from '../../utils/currency';
import { toast } from 'react-toastify';
import { formatPriceSimple } from '../../utils/currency';
import PaymentService from '../../services/payment.service';
import CoupleService from '../../services/couple.service';

// Define the interface for the contribution from verification response
interface ApiContribution {
  _id: string;
  amount: number;
  contributorName: string;
  contributorEmail?: string;
  message?: string;
  type?: 'gift' | 'wedding_fund' | 'honeymoon' | 'other';
  couple?: string;
  coupleObject?: Couple;
  paystackReference?: string;
  confirmed?: boolean;
  currency: SupportedCurrency;
  createdAt?: string;
  updatedAt?: string;
}

// Define the interface for verification response
interface VerificationResponse {
  success: boolean;
  message: string;
  needsVerification?: boolean;
  isPremium?: boolean;
  premiumPlan?: string;
  isSubscription?: boolean;
  isContribution?: boolean;
  contribution?: ApiContribution;
  plan?: {
    type: string;
    duration: string;
  };
  email?: string;
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contribution, setContribution] = useState<Contribution | null>(null);
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const [coupleObject, setCoupleObject] = useState<Couple | null>(null);
  
  useEffect(() => {
    const fetchVerificationDetails = async () => {
      try {
        setLoading(true);
        const reference = searchParams.get('reference');
        
        if (!reference) {
          setError('Payment reference not found');
          setLoading(false);
          return;
        }
        
        const response = await PaymentService.publicVerifyTransaction(reference) as VerificationResponse;
        
        if (response.success && response.contribution) {
          // First check if we need to fetch the couple details
          let coupleId = response.contribution.couple;
          let coupleDetails: Couple | null = null;
          
          if (coupleId) {
            try {
              // Fetch couple details if we have an ID
              coupleDetails = await CoupleService.getCoupleById(coupleId);
              setCoupleObject(coupleDetails);
              setCustomUrl(coupleDetails.customUrl);
            } catch (err) {
              console.error('Error fetching couple details:', err);
            }
          }
          
          // Convert the API contribution to match the Contribution interface
          const fullContribution: Contribution = {
            _id: response.contribution._id,
            amount: response.contribution.amount,
            contributorName: response.contribution.contributorName,
            contributorEmail: response.contribution.contributorEmail,
            message: response.contribution.message,
            type: response.contribution.type || 'wedding_fund',
            couple: response.contribution.couple || '',
            paystackReference: response.contribution.paystackReference,
            confirmed: response.contribution.confirmed || true,
            currency: response.contribution.currency || 'NGN',
            createdAt: response.contribution.createdAt || new Date().toISOString(),
            updatedAt: response.contribution.updatedAt || new Date().toISOString()
          };
          
          setContribution(fullContribution);
        } else {
          setError(response.message || 'Failed to verify payment.');
        }
      } catch (err: any) {
        console.error('Verification error:', err);
        setError(err.response?.data?.message || err.message || 'An error occurred while verifying your payment.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVerificationDetails();
  }, [searchParams]);

  // Format the amount considering if it needs to be divided by 100
  const formatAmount = (amount: number, currency: SupportedCurrency = 'NGN') => {
    // Some payment processors return amount in smallest currency unit (cents/kobo)
    // Determine if we need to divide by 100 based on the size of the amount
    const divisor = amount > 10000 ? 100 : 1; // Heuristic - if amount is large, assume it's in smallest unit
    return formatPriceSimple(amount / divisor, currency);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white to-[#FFF8E7] p-4">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Verifying your payment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white to-[#FFF8E7] p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="bg-red-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Payment Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          
          <div className="space-y-3">
            <Link
              to="/"
              className="bg-pink-600 text-white font-medium py-2 px-6 rounded-full hover:bg-pink-700 transition-colors inline-block w-full"
            >
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white to-[#FFF8E7] p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="bg-green-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h1>
        
        <p className="text-gray-600 mb-6">
          Your contribution to the couple has been successfully processed.
        </p>
        
        {contribution && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-medium text-gray-800 mb-2">Contribution Details</h3>
            <p className="text-gray-600">
              <span className="font-medium">Amount:</span>{' '}
              {formatAmount(contribution.amount, contribution.currency)}
            </p>
            <p className="text-gray-600">
              <span className="font-medium">From:</span> {contribution.contributorName}
            </p>
            {contribution.type && (
              <p className="text-gray-600">
                <span className="font-medium">Type:</span> {contribution.type.replace('_', ' ').charAt(0).toUpperCase() + contribution.type.replace('_', ' ').slice(1)}
              </p>
            )}
            {contribution.message && (
              <p className="text-gray-600">
                <span className="font-medium">Message:</span> {contribution.message}
              </p>
            )}
          </div>
        )}
        
        <div className="space-y-3">
          {customUrl ? (
            <Link
              to={`/wedding/${customUrl}`}
              className="bg-pink-600 text-white font-medium py-2 px-6 rounded-full hover:bg-pink-700 transition-colors inline-block w-full"
            >
              Return to Couple's Page
            </Link>
          ) : (
            <Link
              to="/"
              className="bg-pink-600 text-white font-medium py-2 px-6 rounded-full hover:bg-pink-700 transition-colors inline-block w-full"
            >
              Return to Homepage
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;